import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { parseApiError } from '@/common/utils';
import {
  pauseCRMContactFollowUp,
  resumeCRMContactFollowUp,
  stopCRMContactFollowUp,
  rescheduleCRMContactFollowUp,
} from '../../crm/api';
import { reminderKeys } from './useReminders';
import {
  getReminderContactId,
  getReminderSourceId,
} from '../utils/reminderActions';

function followUpVars(companyId, reminder) {
  return {
    companyId,
    contactId: getReminderContactId(reminder),
    followUpId: getReminderSourceId(reminder),
  };
}

function assertFollowUpTarget(vars) {
  if (!vars.companyId || vars.contactId == null || vars.followUpId == null) {
    throw new Error('Contact not in CRM — cannot update this reminder.');
  }
}

function actionErrorMessage(err, fallback) {
  if (err?.response) {
    const { message } = parseApiError(err);
    return message || fallback;
  }
  return err?.message || fallback;
}

export function useReminderFollowUpActions(companyId, { onDone } = {}) {
  const queryClient = useQueryClient();
  const finish = () => onDone?.();

  const refetchReminders = (reminder) => {
    queryClient.invalidateQueries({ queryKey: reminderKeys.all });
    queryClient.invalidateQueries({ queryKey: ['reminders-summary'] });
    if (reminder?.id) {
      queryClient.invalidateQueries({
        queryKey: reminderKeys.detail(companyId, reminder.id),
      });
    } else {
      queryClient.invalidateQueries({
        queryKey: ['reminders-detail', companyId],
      });
    }

    const contactId = getReminderContactId(reminder);
    if (contactId) {
      queryClient.invalidateQueries({
        queryKey: ['crm-contact-follow-ups', companyId, contactId],
      });
      queryClient.invalidateQueries({
        queryKey: ['crm-contact-follow-up-detail', companyId, contactId],
      });
    }
  };

  const stopMutation = useMutation({
    mutationFn: (reminder) => {
      const vars = followUpVars(companyId, reminder);
      assertFollowUpTarget(vars);
      return stopCRMContactFollowUp(vars);
    },
    onSuccess: (_, reminder) => {
      toast.success('Reminder stopped.');
      refetchReminders(reminder);
      finish();
    },
    onError: (err, reminder) => {
      toast.error(actionErrorMessage(err, 'Failed to stop reminder.'));
      refetchReminders(reminder);
    },
  });

  const pauseMutation = useMutation({
    mutationFn: (reminder) => {
      const vars = followUpVars(companyId, reminder);
      assertFollowUpTarget(vars);
      return pauseCRMContactFollowUp(vars);
    },
    onSuccess: (_, reminder) => {
      toast.success('Reminder paused.');
      refetchReminders(reminder);
      finish();
    },
    onError: (err, reminder) => {
      toast.error(actionErrorMessage(err, 'Failed to pause reminder.'));
      refetchReminders(reminder);
    },
  });

  const resumeMutation = useMutation({
    mutationFn: (reminder) => {
      const vars = followUpVars(companyId, reminder);
      assertFollowUpTarget(vars);
      return resumeCRMContactFollowUp(vars);
    },
    onSuccess: (_, reminder) => {
      toast.success('Reminder resumed. Next send is now plus the interval.');
      refetchReminders(reminder);
      finish();
    },
    onError: (err, reminder) => {
      toast.error(actionErrorMessage(err, 'Failed to resume reminder.'));
      refetchReminders(reminder);
    },
  });

  const rescheduleMutation = useMutation({
    mutationFn: ({ reminder, newDateTime }) => {
      const vars = followUpVars(companyId, reminder);
      assertFollowUpTarget(vars);
      return rescheduleCRMContactFollowUp({
        ...vars,
        payload: { new_date_time: newDateTime },
      });
    },
    onSuccess: (_, { reminder }) => {
      toast.success('Reminder rescheduled.');
      refetchReminders(reminder);
      finish();
    },
    onError: (err, { reminder } = {}) => {
      toast.error(actionErrorMessage(err, 'Failed to reschedule reminder.'));
      refetchReminders(reminder);
    },
  });

  return {
    stop: stopMutation.mutate,
    pause: pauseMutation.mutate,
    resume: resumeMutation.mutate,
    reschedule: rescheduleMutation.mutate,
    isStopping: stopMutation.isPending,
    isPausing: pauseMutation.isPending,
    isResuming: resumeMutation.isPending,
    isRescheduling: rescheduleMutation.isPending,
    isBusy:
      stopMutation.isPending ||
      pauseMutation.isPending ||
      resumeMutation.isPending ||
      rescheduleMutation.isPending,
  };
}
